import { Flex, Grid, Icon, Separator, Text } from '@chakra-ui/react'
import type { TextProps } from '@chakra-ui/react'
import { Sparkles } from 'lucide-react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'

import { textStyleKeys, textStyles } from '@mangovoice/design-system'
import { CopyButton } from '@mangovoice/mango-ui'

import type { MargoSummaryViewModel } from './MargoSummary.types'

interface MargoSummaryCardProps {
  summary: MargoSummaryViewModel
}

interface MargoSummaryFieldRowProps {
  label: string
  value: string
}

const fieldTextProps = {
  ...textStyles[textStyleKeys.Body3],
  fontWeight: 'medium',
  overflowWrap: 'anywhere',
} satisfies TextProps

const buildClipboardText = ({ summary, fields }: MargoSummaryViewModel) =>
  [summary, ...fields.map(({ label, value }) => `${label}: ${value}`)]
    .filter(Boolean)
    .join('\n\n')

const MargoSummaryFieldRow = ({ label, value }: MargoSummaryFieldRowProps) => (
  <>
    <Text as="dt" {...fieldTextProps}>
      {label}
    </Text>
    <Text as="dd" margin="0" {...fieldTextProps}>
      {value}
    </Text>
  </>
)

export const MargoSummaryCard = ({ summary }: MargoSummaryCardProps) => {
  const { t } = useTranslation()
  const { toastError, toastSuccess } = useToastAlert()
  const titleId = useId()

  return (
    <Flex
      as="section"
      aria-labelledby={titleId}
      bg="warning.subtle"
      borderRadius="0.5rem"
      color="fg.muted"
      flexDirection="column"
      flexShrink="0"
      gap="1rem"
      overflow="hidden"
      padding="0.75rem"
      width="full"
    >
      <Flex flexDirection="column" gap="0.5rem" width="full">
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap="0.5rem" minWidth="0">
            <Icon color="warning.fg" boxSize="1rem" flexShrink="0">
              <Sparkles size="100%" />
            </Icon>
            <Text
              id={titleId}
              color="fg.default"
              {...textStyles[textStyleKeys.Body1_Bold]}
            >
              {t('callDetails.margoSummary.title')}
            </Text>
          </Flex>
          <CopyButton
            value={buildClipboardText(summary)}
            copyAriaLabel={t('callDetails.margoSummary.copy')}
            copiedAriaLabel={t('callDetails.margoSummary.copied')}
            iconSize={16}
            iconStrokeWidth={2.25}
            bg="transparent"
            borderRadius="0.25rem"
            color="fg.muted"
            gap="0.25rem"
            height="2rem"
            minHeight="2rem"
            minWidth="2rem"
            overflow="hidden"
            padding="0"
            variant="ghost"
            width="2rem"
            _active={{ bg: 'transparent', color: 'fg.muted' }}
            _hover={{ bg: 'transparent', color: 'fg.muted' }}
            css={{ '& svg': { color: 'inherit' } }}
            onCopied={() =>
              toastSuccess(t('callDetails.margoSummary.copySuccess'))
            }
            onCopyError={() =>
              toastError(t('callDetails.margoSummary.copyFailure'))
            }
          />
        </Flex>

        {summary.summary && (
          <Text {...textStyles[textStyleKeys.Body3]} fontWeight="medium">
            {summary.summary}
          </Text>
        )}
      </Flex>

      {summary.fields.length > 0 && (
        <>
          <Separator borderColor="warning.muted" />
          <Grid
            as="dl"
            templateColumns="8rem minmax(0, 1fr)"
            columnGap="0.5rem"
            margin="0"
            rowGap="0.5rem"
            width="full"
          >
            {summary.fields.map(({ id, label, value }) => (
              <MargoSummaryFieldRow key={id} label={label} value={value} />
            ))}
          </Grid>
        </>
      )}
    </Flex>
  )
}
